import Box from '@mui/material/Box';
import Link from '@mui/material/Link';

import { PageHeader } from '@/components/ui/PageHeader';
import { useDocumentTitle } from '@/lib/useDocumentTitle';

import { GALLERY_ENTRIES } from './entries';
import { GalleryEntry } from './GalleryEntry';

/**
 * Every shared component in each of its states, on one page, for the mobile
 * developer building the same screens and QA writing the regression checklist.
 *
 * A real routed page rather than a Storybook: it is behind sign-in like
 * everything else, it is swept by the accessibility and width checks like
 * everything else, and it costs no extra dependency, build or deploy. It is
 * listed in no navigation, menu or search - the address is passed on by hand.
 *
 * Everything on it renders sample data and nothing on it reaches a server.
 */
export function ComponentsPage() {
  /*
   * Set here, not only through the PageHeader below: the PageHeader examples
   * further down also name the tab, and a parent's effect runs after its
   * children's - so this one runs last and the tab says where you are.
   */
  useDocumentTitle('Shared components');

  return (
    <Box data-testid="gallery-page" sx={{ maxWidth: 900 }}>
      <PageHeader
        title="Shared components"
        description="Every shared interface piece, in each of its states, over sample data. Nothing here reaches a server."
      />

      <Box component="nav" aria-label="Components">
        <Box
          component="ul"
          data-testid="gallery-contents-list"
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: 2,
            rowGap: 0.5,
          }}
        >
          {GALLERY_ENTRIES.map((entry) => (
            <li key={entry.id}>
              <Link
                href={`#${entry.id}`}
                underline="hover"
                variant="body2"
                data-testid={`gallery-contents-${entry.id}-link`}
              >
                {entry.name}
              </Link>
            </li>
          ))}
        </Box>
      </Box>

      <Box sx={{ mt: 4 }}>
        {GALLERY_ENTRIES.map((entry) => (
          <GalleryEntry key={entry.id} entry={entry} />
        ))}
      </Box>
    </Box>
  );
}
